import { useCallback, useEffect, useRef, useState } from "react"
import { Database, Loader2, Play, Copy, Check, AlertTriangle } from "lucide-react"

// Staging-only admin tool (issue #51): copy production jobs into staging.
// The backend reports enabled:false everywhere except staging, in which case
// this card explains why and offers no actions.
export default function ProdSyncCard({ getApi, onUnauthorized }) {
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [starting, setStarting] = useState(false)
  const [confirmText, setConfirmText] = useState("")
  const [error, setError] = useState("")
  const [copied, setCopied] = useState(false)
  const logRef = useRef(null)
  // Parent passes fresh function instances each render; keep them in refs so
  // `load` stays stable and the effects below don't re-fire in a loop.
  const getApiRef = useRef(getApi)
  const onUnauthorizedRef = useRef(onUnauthorized)
  getApiRef.current = getApi
  onUnauthorizedRef.current = onUnauthorized

  const load = useCallback(async () => {
    try {
      const { data } = await getApiRef.current().get("/admin/prod-sync/status")
      setStatus(data)
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) onUnauthorizedRef.current?.()
      else setError(e.response?.data?.message || e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  // Poll while a run is in progress.
  useEffect(() => {
    if (!status?.running) return
    const t = setInterval(load, 2000)
    return () => clearInterval(t)
  }, [status?.running, load])

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight
  }, [status?.log?.length])

  const start = async (apply) => {
    setError("")
    setStarting(true)
    try {
      await getApiRef.current().post("/admin/prod-sync/run", apply ? { apply: true, confirm: confirmText } : { apply: false })
      setConfirmText("")
      await load()
    } catch (e) {
      setError(e.response?.data?.message || e.response?.data?.error || e.message)
    } finally {
      setStarting(false)
    }
  }

  const copySql = async () => {
    try {
      await navigator.clipboard.writeText(status?.resetSequencesSql || "")
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch { /* clipboard blocked — user can select the text */ }
  }

  const report = status?.report
  const running = !!status?.running

  return (
    <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
        <div className="p-2 bg-blue-50 rounded-lg"><Database size={20} className="text-blue-600" /></div>
        <div>
          <h2 className="text-base font-semibold text-gray-900">Copy production jobs to staging</h2>
          <p className="text-xs text-gray-500">
            Adds production jobs, customers, team and invoices that staging doesn't have. Never deletes or overwrites staging data.
          </p>
        </div>
      </div>

      <div className="p-6 space-y-4">
        {loading ? (
          <div className="text-center"><Loader2 size={22} className="animate-spin mx-auto text-gray-400" /></div>
        ) : !status?.enabled ? (
          <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3">
            Not available on this environment{status?.reason ? ` — ${status.reason}.` : "."}
          </div>
        ) : (
          <>
            <div className="text-sm text-gray-700">
              From production <code className="px-1.5 py-0.5 bg-gray-100 rounded">{status.prodRef}</code> into staging{" "}
              <code className="px-1.5 py-0.5 bg-gray-100 rounded">{status.stagingRef}</code>. Production is only read.
              Card keys and SMS tokens are stripped and customer phones/emails are replaced with fake ones, so staging
              can't charge real cards or message real customers.
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => start(false)}
                disabled={running || starting}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                <Play size={14} /> Dry run (report only)
              </button>
              <div className="flex items-center gap-2">
                <input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={`Type ${status.stagingRef} to copy`}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-64"
                  disabled={running}
                />
                <button
                  onClick={() => start(true)}
                  disabled={running || starting || confirmText.trim() !== status.stagingRef}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  {running && status.mode === "apply" ? <Loader2 size={14} className="animate-spin" /> : <Database size={14} />}
                  Copy to staging
                </button>
              </div>
              {running && (
                <span className="inline-flex items-center gap-1.5 text-xs text-blue-700 bg-blue-50 px-3 py-1 rounded-full">
                  <Loader2 size={12} className="animate-spin" /> {status.mode === "apply" ? "Copying…" : "Dry run in progress…"}
                </span>
              )}
            </div>

            {error && (
              <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
                <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" /> {error}
              </div>
            )}
            {status.error && !running && (
              <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
                <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" /> Last run failed: {status.error}
              </div>
            )}

            {report && !running && (
              <div className="overflow-x-auto">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
                  {report.applied ? "Copied" : "Dry run — would copy"} · finished {status.finishedAt ? new Date(status.finishedAt).toLocaleString() : ""}
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 border-b">
                      <th className="py-1.5 pr-3">Table</th>
                      <th className="py-1.5 pr-3 text-right">{report.applied ? "Added" : "Would add"}</th>
                      <th className="py-1.5 pr-3 text-right">Already on staging</th>
                      <th className="py-1.5 pr-3 text-right">Skipped</th>
                      <th className="py-1.5 text-right">Failed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.tables.map((t) => (
                      <tr key={t.table} className="border-b border-gray-50">
                        <td className="py-1.5 pr-3 font-mono text-xs">{t.table}</td>
                        <td className="py-1.5 pr-3 text-right font-semibold">{t.added}</td>
                        <td className="py-1.5 pr-3 text-right text-gray-500">{t.already}</td>
                        <td className="py-1.5 pr-3 text-right text-gray-500" title="ID used by another account / parent skipped / account not on staging">
                          {t.collided + t.skippedParent + t.skippedNoAccount}
                        </td>
                        <td className={`py-1.5 text-right ${t.failed ? "text-red-600 font-semibold" : "text-gray-400"}`}>{t.failed}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {report.accounts?.some((a) => a.stagingId == null) && (
                  <p className="text-xs text-amber-700 mt-2">
                    Accounts not on staging (their data is skipped):{" "}
                    {report.accounts.filter((a) => a.stagingId == null).map((a) => a.email).join(", ")}
                  </p>
                )}
              </div>
            )}

            {report?.applied && !running && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                <div className="text-sm text-amber-800 font-medium">
                  Last step: run this once in the <b>staging</b> Supabase SQL editor, so new jobs created on staging don't fail with "duplicate key".
                </div>
                <div className="flex justify-end">
                  <button onClick={copySql} className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-white border border-amber-300 hover:bg-amber-100">
                    {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy SQL"}
                  </button>
                </div>
                <pre className="text-[11px] bg-white border border-amber-200 rounded p-2 overflow-x-auto">{status.resetSequencesSql}</pre>
              </div>
            )}

            {status.log?.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Log</div>
                <pre ref={logRef} className="text-[11px] bg-gray-900 text-gray-100 rounded-lg p-3 max-h-64 overflow-y-auto whitespace-pre-wrap">
                  {status.log.join("\n")}
                </pre>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  )
}
